import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import imageCompression from 'browser-image-compression';
import { Upload, X } from 'lucide-react';

const ProductEdit = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;
    const token = localStorage.getItem('adminToken');

    const [form, setForm] = useState({ 
        title: '', 
        description: '', 
        price: '', 
        category: 'web', 
        link_demo: '', 
        image_url: '', 
        is_new: false, 
        is_hit: false 
    });
    const [imageUrls, setImageUrls] = useState([]);
    const [uploading, setUploading] = useState(false);

    const parseImageUrls = (value) => {
        if (!value) return [];
        if (Array.isArray(value)) return value.filter(Boolean);

        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (!trimmed) return [];

            if (trimmed.startsWith('[')) {
                try {
                    const parsed = JSON.parse(trimmed);
                    if (Array.isArray(parsed)) return parsed.filter(Boolean);
                } catch (error) {
                    return [trimmed];
                }
            }

            return [trimmed];
        }

        return [];
    };

    useEffect(() => {
        if (isEdit) {
            axios.get(`/api/products/${id}`)
                .then(res => {
                    setForm(res.data);
                    setImageUrls(parseImageUrls(res.data.image_url));
                })
                .catch(err => {
                    toast.error('Ошибка загрузки товара');
                    navigate('/admin/products');
                });
        }
    }, [id, isEdit, navigate]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
    }

    const handleImageUpload = async (event) => {
        const selectedFiles = Array.from(event.target.files || []);
        if (selectedFiles.length === 0) return;

        const availableSlots = 15 - imageUrls.length;
        if (availableSlots <= 0) {
            toast.error('Максимум 15 фото для одного товара');
            event.target.value = '';
            return;
        }

        const filesToUpload = selectedFiles.slice(0, availableSlots);
        if (selectedFiles.length > availableSlots) {
            toast.error(`Можно загрузить только ${availableSlots} фото (лимит 15)`);
        }

        setUploading(true);
        try {
            const compressedFiles = [];
            for (const file of filesToUpload) {
                const compressed = await imageCompression(file, {
                    maxSizeMB: 1,
                    maxWidthOrHeight: 1200,
                    useWebWorker: true
                });
                compressedFiles.push(compressed);
            }

            const formData = new FormData();
            compressedFiles.forEach((file) => {
                formData.append('images', file);
            });

            const res = await axios.post('/api/upload', formData, {
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            const uploadedUrls = Array.isArray(res.data.urls)
                ? res.data.urls
                : (res.data.url ? [res.data.url] : []);

            const nextUrls = [...imageUrls, ...uploadedUrls].slice(0, 15);
            setImageUrls(nextUrls);
            setForm((prev) => ({
                ...prev,
                image_url: nextUrls.length > 1 ? JSON.stringify(nextUrls) : (nextUrls[0] || '')
            }));
            toast.success(`Загружено фото: ${uploadedUrls.length}`);
        } catch (error) {
            console.error(error);
            toast.error('Ошибка при загрузке фото');
        } finally {
            setUploading(false);
            event.target.value = '';
        }
    };

    const removeImageAt = (index) => {
        const nextUrls = imageUrls.filter((_, i) => i !== index);
        setImageUrls(nextUrls);
        setForm((prev) => ({
            ...prev,
            image_url: nextUrls.length > 1 ? JSON.stringify(nextUrls) : (nextUrls[0] || '')
        }));
    };

    const handleImageUrlManualChange = (e) => {
        const value = e.target.value;
        const parsedUrls = parseImageUrls(value).slice(0, 15);
        setForm((prev) => ({ ...prev, image_url: value }));
        setImageUrls(parsedUrls);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (isEdit) {
                await axios.put(`/api/products/${id}`, form, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Товар успешно обновлен!');
            } else {
                await axios.post('/api/products', form, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Товар успешно создан!');
            }
            navigate('/admin/products');
        } catch (err) {
            console.error(err);
            toast.error('Произошла ошибка при сохранении.');
        }
    }

    return (
        <div className="admin-page">
            <h2>{isEdit ? `✏️ Редактирование #${id}` : '➕ Добавить товар'}</h2>
            
            <form onSubmit={handleSubmit} className="admin-form">
                <div className="form-group">
                    <label>Название</label>
                    <input type="text" name="title" placeholder="Например: Бот автопродаж" value={form.title} onChange={handleChange} required />
                </div>
                
                <div className="form-group">
                    <label>Описание</label>
                    <textarea name="description" rows="4" placeholder="Краткое описание" value={form.description} onChange={handleChange} required />
                </div>

                <div className="form-row mobile-col">
                    <div className="form-group half">
                        <label>Цена (в сомони TJS)</label>
                        <input type="number" name="price" placeholder="150" value={form.price} onChange={handleChange} required />
                    </div>
                    <div className="form-group half">
                        <label>Категория</label>
                        <select name="category" value={form.category} onChange={handleChange}>
                            <option value="web">Сайт (Web)</option>
                            <option value="bot">TG Бот (Bot)</option>
                            <option value="script">Скрипт (Script)</option>
                            <option value="design">Дизайн (Design)</option>
                            <option value="database">База (Database)</option>
                            <option value="other">Разное (Other)</option>
                        </select>
                    </div>
                </div>

                <div className="form-group">
                    <label>Изображения (до 15 шт.)</label>
                    <div className="image-uploader">
                        {imageUrls.length > 0 && (
                            <div className="preview-grid">
                                {imageUrls.map((url, idx) => (
                                    <div className="preview-wrap" key={`${url}-${idx}`}>
                                        <img src={url} alt={`Preview ${idx + 1}`} className="img-preview" />
                                        <button type="button" onClick={() => removeImageAt(idx)} className="btn-remove-img"><X size={16}/></button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <label className={`upload-btn ${uploading ? 'disabled' : ''}`}>
                            <Upload size={20} />
                            {uploading ? 'Сжимаем и грузим...' : `Выберите фото (${imageUrls.length}/15)`}
                            <input type="file" onChange={handleImageUpload} accept="image/*" disabled={uploading || imageUrls.length >= 15} multiple hidden />
                        </label>
                    </div>
                    <input type="text" name="image_url" placeholder='Или вставьте JSON/URL (например ["https://..."])' value={form.image_url} onChange={handleImageUrlManualChange} style={{marginTop: '10px'}}/>
                </div>

                <div className="form-group">
                     <label>Ссылка на демо</label>
                     <input type="url" name="link_demo" placeholder="https://..." value={form.link_demo} onChange={handleChange} />
                </div>

                <div className="checkbox-group">
                    <label><input type="checkbox" name="is_new" checked={form.is_new} onChange={handleChange} /> NEW</label>
                    <label><input type="checkbox" name="is_hit" checked={form.is_hit} onChange={handleChange} /> ХИТ</label>
                </div>

                <div className="form-actions mobile-col">
                    <button type="button" onClick={() => navigate('/admin/products')} className="btn-cancel">Отмена</button>
                    <button type="submit" className="btn-submit" disabled={uploading}>
                        {isEdit ? 'Сохранить изменения' : 'Создать товар'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ProductEdit;
